import { ExpandMore } from "@mui/icons-material";
import { Alert, Avatar, Box, Card, CardContent, Collapse, Grid, Hidden, Icon, IconButton, LinearProgress, Link, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/fomTemplateType.enum";
import { IUserCartData } from "../../_common/interface/userCart/iUserCartList";
import { IUserProfileManage } from "../../_common/interface/users/iUserProfileManage";
import { LIGHT_GREY_COLOR, ORANGE_COLOR, PRIMARY_COLOR } from "../../_const/color.const";
import { getDate, getFutureDate } from "../../_helpers/dateTime.helper";
import { getMoneyFormat } from "../../_helpers/format.helper";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { accountService } from "../../_services/accounts.service";
import { formTemplateService } from "../../_services/formTemplate.service";
import { userInformationService } from "../../_services/userInformation.service";
import { InfoManageLicenseButton } from "./user-infoManageLicenseButton";
import { UserProfilePageLicenseList } from "./user-profilePageLicenseList";
import { FormStatusEnum } from "../../_common/enum/forms/formStatus.enum";
import { InfoManageCreditButton } from "./user-infoManageCreditButton";

var QRCode = require('qrcode')

export function UserProfilePageOrganiser() {

    const { userId, licenseType } = useParams<{ userId: string, licenseType: string }>();

    let navigate = useNavigate();

    const resetData = () => ({
        id: "",
        firstName: '',
        lastName: '',
        name: '',
        email: '',
        phone: '',
        country: '',
        address: '',
        county: '',
        postalCode: '',
        username: '',
        genderId: null,
        dateOfBirth: null,
        nationality: null,
        fines: []
    });

    const resetUserCartData = () => ({
        id: 0,
        isActive: false,
        paidDate: ""
    });

    const [checked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState<IUserProfileManage>(resetData);
    const [userProfileImg, setUserProfileImg] = useState();
    const [userCartData, setUserCartData] = useState<IUserCartData>(resetUserCartData);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    const [isLicensesOpen, setIsLicensesOpen] = useState(false);
    const [isInsurancesOpen, setIsInsurancesOpen] = useState(false);

    const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
    const [isQuestionnaireLoading, setIsQuestionnaireLoading] = useState(true);
    const [questionnaires, setQuestionnaires] = useState([]);

    useEffect(() => {
        QRCode.toCanvas(document.getElementById('canvas'), window.location.href, {
            margin: 2,
        }, (error: any) => {
            if (error) console.error(error)
        })
    }, []);

    useEffect(() => {
        if (data) {
            document.title = data.lastName + " " + data.firstName;
        }
    }, [data]);

    useEffect(() => {
        return () => {
            document.title = "Motorsport Ireland";
        };
    }, []);

    useEffect(() => {
        fetchData(userId || accountService.currentUserValue.id);
        fetchUserImg(userId || accountService.currentUserValue.id);
    }, []);

    useEffect(() => {
        if (isQuestionnaireOpen && !questionnaires.length) {
            fetchQuestionnaires();
        }
    }, [isQuestionnaireOpen]);

    function fetchQuestionnaires() {

        setIsQuestionnaireLoading(true)
        return formTemplateService
            .getAllByType(FormTemplateTypeEnum.Questionnaire, FormStatusEnum.Approved)
            .then(
                (json) => {
                    setQuestionnaires(json)
                }, handleErrorResponse
            )
            .finally(() => {
                setIsQuestionnaireLoading(false);
            });
    }

    function fetchData(id: string) {

        setIsLoading(true)
        return userInformationService
            .get(id)
            .then(
                (json) => {
                    setData(_data => { _data.role = json.roles; return { ..._data, ...json } });
                    if (json.fines.length > 0) {
                        setUserCartData(__data => {
                            __data.id = json.fines[json.fines.length - 1].userCartId;
                            __data.isActive = json.fines[0].userCartIsActive;
                            __data.paidDate = json.fines[json.fines.length - 1].userCartPaidDate;
                            return { ...__data }
                        });

                    }
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function fetchUserImg(id: string) {

        setIsLoading(true)
        return userInformationService
            .getProfileImage(id)
            .then(
                (json) => {
                    setUserProfileImg(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    const totalFinesAmount = data.fines.filter(fine => !fine.isPaid && fine.isChargeable).reduce((acc, fine) => acc + fine.price, 0);
    const totalDurationBan = data.fines.reduce((acc, fine) => acc + (fine.durationBan || 0), 0);
    const isAnyIndefinitelyBanned = data.fines.some(fine => fine.isBannedIndefinitely);

    return (
        <>
            {/* <AppBarUser navItems={filteredUserNavs} /> */}
            <Grid container sx={{ maxWidth: 'xl', margin: { xs: '0px auto', md: '20px auto' } }}>


                <Hidden mdDown>
                    <Grid item xs={12} sm={3}>

                        <Grid item xs={12}>
                            <Card sx={{ height: '350px', position: 'relative', display: 'flex', flexDirection: 'column', marginBottom: '10px' }}>
                                <Box
                                    sx={{
                                        height: '150px',
                                        backgroundColor: PRIMARY_COLOR[1],
                                        position: 'relative',
                                    }} />

                                <Avatar
                                    alt="User Name"
                                    src={"data:image/jpeg;charset=utf-8;base64," + userProfileImg}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        position: 'absolute',
                                        top: '150px',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        border: '2px solid white',
                                        borderRadius: "0px"
                                    }} />

                                <CardContent sx={{ textAlign: 'center', mt: '60px' }}>
                                    <Typography variant="h5" color={PRIMARY_COLOR[1]}>{data.firstName} {data.lastName}</Typography>
                                    <Typography variant="body1"><a href={`mailto:${data.email}`}>{data.email}</a></Typography>
                                    <Typography variant="body1">{data.dateOfBirth ? getDate(data.dateOfBirth) : "-"} ({data.age ? data.age + "yo" : "???"})  {accountService.isAdmin && <IconButton color="inherit" onClick={() => { navigator.clipboard.writeText(data.dateOfBirth ? getDate(data.dateOfBirth) : ""); }}><Icon>content_copy</Icon></IconButton>}</Typography>

                                    {accountService.isAdmin &&
                                        <Typography><span style={{ color: ORANGE_COLOR }}>Status:</span> {data.userStatusName}</Typography>
                                    }

                                </CardContent>
                            </Card>
                        </Grid>


                        <Grid item xs={12} mb="8px">
                            <InfoManageCreditButton />
                        </Grid>

                        <Grid item xs={12} mb="8px">
                            <UserProfilePageLicenseList />
                        </Grid>


                    </Grid>
                </Hidden>

                <Grid item xs={12} md={9} style={{ paddingLeft: "0px" }}>
                    <Grid container style={{ padding: "0px" }}>


                        <Hidden mdUp>
                            <Grid item xs={12} md={12} style={{ padding: "0px" }}>
                                <Outlet></Outlet>
                            </Grid>
                        </Hidden>

                        <Hidden mdDown>
                            <Grid item xs={12} md={12} style={{ padding: "8px" }}>
                                <Outlet></Outlet>
                            </Grid>
                        </Hidden>
                    </Grid>

                </Grid>

            </Grid >
        </>
    );

}
